import React from 'react';
import { Card, CardContent } from './ui/card';
import { MapPin, Heart, Package, Users, Clock, Shield } from 'lucide-react';

const AboutSection = ({ language }) => {
  const content = language === 'en' ? {
    title: "About Athena's",
    subtitle: "Your Trusted Shopping Partner in Belize",
    intro: "Welcome to Athena's Online Shopping Services! We are a proud Belizean business based in Orange Walk, dedicated to making international online shopping accessible and hassle-free for everyone in Belize.",
    
    mission: {
      title: "Our Mission",
      text: "To become your one-stop shop for all your shopping needs, bringing the world's best products right to your doorstep in Belize."
    },
    
    story: {
      title: "Our Story",
      text: "We understand the challenges Belizeans face when trying to shop from international online stores. That's why we created Athena's - to bridge that gap and give you access to thousands of products from top USA retailers like Amazon, Nike, SHEIN, Zara, and many more."
    },
    
    values: [
      {
        icon: Heart,
        title: "Customer First",
        desc: "Your satisfaction is our top priority. We treat every order with care and attention."
      },
      {
        icon: Shield,
        title: "Trustworthy",
        desc: "As a local Belizean business, we're here for you with transparent pricing and reliable service."
      },
      {
        icon: Package,
        title: "Quality Service",
        desc: "From purchase to delivery, we handle everything so you can shop with confidence."
      },
      {
        icon: Clock,
        title: "Fast & Reliable",
        desc: "Consistent 3-week delivery timeframe with tracking updates throughout the journey."
      },
      {
        icon: Users,
        title: "Community Focused",
        desc: "We're your neighbors in Orange Walk, serving families across Belize with care."
      },
      {
        icon: MapPin,
        title: "Local Presence",
        desc: "Based in Orange Walk, we understand the unique needs of Belizean shoppers."
      }
    ],
    
    commitment: {
      title: "Our Commitment to You",
      text: "Whether you're shopping for fashion, electronics, home goods, or anything in between, Athena's is here to make it happen. We believe every Belizean deserves access to the products they love, and we're committed to providing that access with exceptional service, competitive prices, and the personal touch of a local business."
    },
    
    closing: "Thank you for choosing Athena's. We look forward to serving you and becoming your trusted shopping partner!",
    team: "- The Athena's Team, Orange Walk, Belize"
  } : {
    title: "Acerca de Athena's",
    subtitle: "Su Socio de Compras de Confianza en Belice",
    intro: "¡Bienvenido a Athena's Online Shopping Services! Somos un negocio beliceño orgulloso con sede en Orange Walk, dedicado a hacer que las compras en línea internacionales sean accesibles y sin complicaciones para todos en Belice.",
    
    mission: {
      title: "Nuestra Misión",
      text: "Convertirnos en su ventanilla única para todas sus necesidades de compras, trayendo los mejores productos del mundo directamente a su puerta en Belice."
    },
    
    story: {
      title: "Nuestra Historia",
      text: "Entendemos los desafíos que enfrentan los beliceños al intentar comprar en tiendas en línea internacionales. Por eso creamos Athena's - para cerrar esa brecha y darle acceso a miles de productos de los principales minoristas de EE. UU. como Amazon, Nike, SHEIN, Zara y muchos más."
    },
    
    values: [
      {
        icon: Heart,
        title: "Cliente Primero",
        desc: "Su satisfacción es nuestra máxima prioridad. Tratamos cada pedido con cuidado y atención."
      },
      {
        icon: Shield,
        title: "Confiable",
        desc: "Como un negocio local beliceño, estamos aquí para usted con precios transparentes y servicio confiable."
      },
      {
        icon: Package,
        title: "Servicio de Calidad",
        desc: "Desde la compra hasta la entrega, manejamos todo para que pueda comprar con confianza."
      },
      {
        icon: Clock,
        title: "Rápido y Confiable",
        desc: "Plazo de entrega constante de 3 semanas con actualizaciones de seguimiento durante todo el viaje."
      },
      {
        icon: Users,
        title: "Enfocado en la Comunidad",
        desc: "Somos sus vecinos en Orange Walk, sirviendo a familias en todo Belice con cuidado."
      },
      {
        icon: MapPin,
        title: "Presencia Local",
        desc: "Con sede en Orange Walk, entendemos las necesidades únicas de los compradores beliceños."
      }
    ],
    
    commitment: {
      title: "Nuestro Compromiso con Usted",
      text: "Ya sea que esté comprando moda, electrónica, artículos para el hogar o cualquier otra cosa, Athena's está aquí para hacerlo realidad. Creemos que cada beliceño merece acceso a los productos que ama, y estamos comprometidos a proporcionar ese acceso con un servicio excepcional, precios competitivos y el toque personal de un negocio local."
    },
    
    closing: "Gracias por elegir Athena's. ¡Esperamos servirle y convertirnos en su socio de compras de confianza!",
    team: "- El Equipo de Athena's, Orange Walk, Belice"
  };

  return (
    <section id="about" className="py-20 px-4 bg-gradient-to-b from-[#f7f5f2] to-white">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-4">
            {content.title}
          </h2>
          <p className="text-xl text-[#8b1538] font-semibold">
            {content.subtitle}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-12 border-2 border-[#8b1538]/20">
          <CardContent className="p-8">
            <p className="text-lg text-[#1e1919] leading-relaxed">
              {content.intro}
            </p>
          </CardContent>
        </Card>

        {/* Mission & Story */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="border-2 border-[#fad24b]/30 hover:border-[#fad24b] transition-all">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-[#8b1538] mb-4">
                {content.mission.title}
              </h3>
              <p className="text-[#736c64] leading-relaxed">
                {content.mission.text}
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#3dd3ee]/30 hover:border-[#3dd3ee] transition-all">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-[#8b1538] mb-4">
                {content.story.title}
              </h3>
              <p className="text-[#736c64] leading-relaxed">
                {content.story.text}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-center text-[#1e1919] mb-10">
            {language === 'en' ? 'What Makes Us Different' : 'Lo Que Nos Hace Diferentes'}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-2 border-transparent hover:border-[#8b1538]/30 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#8b1538]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-[#8b1538]" />
                    </div>
                    <h4 className="text-lg font-bold text-[#1e1919] mb-2">
                      {value.title}
                    </h4>
                    <p className="text-sm text-[#736c64]">
                      {value.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Commitment */}
        <Card className="mb-8 border-2 border-[#ff8c19]/30">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-[#8b1538] mb-4">
              {content.commitment.title}
            </h3>
            <p className="text-[#1e1919] leading-relaxed mb-6">
              {content.commitment.text}
            </p>
            <p className="text-lg text-[#1e1919] font-semibold italic">
              {content.closing}
            </p>
            <p className="text-[#8b1538] font-semibold mt-4">
              {content.team}
            </p>
          </CardContent>
        </Card>

        {/* Location Highlight */}
        <div className="text-center p-8 bg-gradient-to-r from-[#8b1538]/10 to-[#fad24b]/10 rounded-lg">
          <MapPin className="w-12 h-12 text-[#8b1538] mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-[#1e1919] mb-2">
            {language === 'en' ? 'Proudly Serving Belize from Orange Walk' : 'Orgullosamente Sirviendo a Belice desde Orange Walk'}
          </h3>
          <p className="text-[#736c64] max-w-2xl mx-auto">
            {language === 'en' 
              ? 'As a local Belizean business, we understand your needs and are committed to serving our community with excellence.'
              : 'Como un negocio local beliceño, entendemos sus necesidades y estamos comprometidos a servir a nuestra comunidad con excelencia.'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
